import { useEffect, useRef, useState } from "react";
import { X, Sparkles, Gauge, Type, Smile, Check, Trash2, Palette, Clock, Zap, ImagePlay } from "lucide-react";

/* ==========================================================================
 * VideoEditor — Instagram/Moj-tier text + emoji + GIF overlay editor.
 * ==========================================================================
 * Backwards-compat: edits schema stays { filter_id, filter_css, speed, overlays }.
 * Each overlay is enriched with (all optional, default sensible):
 *   font, color, fontSize, animation, start_time, end_time
 * ReelsFeed.jsx renders overlays using these fields.
 * ========================================================================== */

const FILTERS = [
  { id: "none",    name: "Original", css: "none" },
  { id: "vivid",   name: "Vivid",    css: "saturate(1.5) contrast(1.15)" },
  { id: "warm",    name: "Warm",     css: "sepia(0.25) saturate(1.2) hue-rotate(-10deg)" },
  { id: "cool",    name: "Cool",     css: "saturate(1.1) hue-rotate(180deg) brightness(1.05) contrast(1.05)" },
  { id: "bw",      name: "B&W",      css: "grayscale(1) contrast(1.05)" },
  { id: "vintage", name: "Vintage",  css: "sepia(0.4) saturate(1.15) contrast(0.95) brightness(1.05)" },
  { id: "cinema",  name: "Cinema",   css: "saturate(0.85) contrast(1.2) brightness(0.95) hue-rotate(-5deg)" },
];

// 40+ emojis grouped for easy pickup
const STICKERS = [
  "❤️","🔥","✨","🎉","💯","👀","😂","🌟","💖","🤩","🎵","🎬",
  "🥰","😍","😎","🤗","🤔","🙌","👏","💪","🕺","💃","🎯","💰",
  "🏆","👑","🎨","🎤","🎸","🍕","🌈","⚡","💥","💫","🌸","🌺",
  "🚀","🎁","🍾","🥂","👉","👇","😜","🤪","🥳"
];

// 6 font families for text overlays
const FONTS = [
  { id: "outfit",      name: "Outfit",     css: "'Outfit', sans-serif", weight: 800 },
  { id: "serif",       name: "Serif",      css: "Georgia, 'Times New Roman', serif", weight: 700 },
  { id: "mono",        name: "Mono",       css: "'Courier New', monospace", weight: 700 },
  { id: "handwriting", name: "Handwrite",  css: "'Brush Script MT', cursive", weight: 700 },
  { id: "impact",      name: "Impact",     css: "Impact, 'Arial Black', sans-serif", weight: 900 },
  { id: "comic",       name: "Comic",      css: "'Comic Sans MS', cursive", weight: 700 },
];

// 12 color swatches
const COLORS = [
  "#FFFFFF","#000000","#FFD700","#FF2A5F","#00E5FF","#A855F7",
  "#34D399","#FB923C","#F472B6","#FACC15","#60A5FA","#EF4444",
];

// 6 animation modes (CSS-based, applied at render time in ReelsFeed).
// Kept as plain identifiers so backend/frontend can round-trip safely.
const ANIMATIONS = [
  { id: "none",   name: "None"   },
  { id: "fade",   name: "Fade"   },
  { id: "bounce", name: "Bounce" },
  { id: "pulse",  name: "Pulse"  },
  { id: "spin",   name: "Spin"   },
  { id: "shake",  name: "Shake"  },
];

// 12 curated animated GIF stickers hosted on Tenor's public CDN (no auth key needed).
// Users are shown a small "Add GIF" library — great creative surface without a paid API.
// If a GIF fails to load, we gracefully degrade (broken <img> is invisible on video).
const GIFS = [
  { id: "gif-fire",    label: "Fire",      url: "https://media.tenor.com/pACOlaisMEwAAAAi/fire.gif" },
  { id: "gif-heart",   label: "Heart",     url: "https://media.tenor.com/9F_Yh3HRmxYAAAAi/heart-love.gif" },
  { id: "gif-hearts",  label: "Hearts",    url: "https://media.tenor.com/9SnhkX4wxfoAAAAi/heart-hearts.gif" },
  { id: "gif-star",    label: "Star",      url: "https://media.tenor.com/9GdEXCiFH1QAAAAi/star-sparkle.gif" },
  { id: "gif-love",    label: "Love",      url: "https://media.tenor.com/kOF3fh3Xk4YAAAAi/love-heart.gif" },
  { id: "gif-clap",    label: "Clap",      url: "https://media.tenor.com/-i2s0jLM1EEAAAAi/clapping.gif" },
  { id: "gif-crown",   label: "Crown",     url: "https://media.tenor.com/DjBQ7cwEDp8AAAAi/crown.gif" },
  { id: "gif-money",   label: "Money",     url: "https://media.tenor.com/lQoBhg7ArgwAAAAi/money-cash.gif" },
  { id: "gif-100",     label: "100",       url: "https://media.tenor.com/8ecXtvIhV20AAAAi/100.gif" },
  { id: "gif-boom",    label: "Boom",      url: "https://media.tenor.com/OZlPjcSlvI0AAAAi/boom-explosion.gif" },
  { id: "gif-party",   label: "Party",     url: "https://media.tenor.com/HRlqjT1KyDgAAAAi/party-popper.gif" },
  { id: "gif-lit",     label: "Lit",       url: "https://media.tenor.com/76TflqvxN8IAAAAi/lit-fire.gif" },
];

const uid = () => Math.random().toString(36).slice(2, 10);

export default function VideoEditor({ file, initialEdits, onClose, onSave }) {
  const videoRef = useRef(null);
  const dragRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [tab, setTab] = useState("filter");
  const [filterId, setFilterId] = useState(initialEdits?.filter_id || "none");
  const [speed, setSpeed] = useState(initialEdits?.speed || 1);
  const [overlays, setOverlays] = useState(initialEdits?.overlays || []);
  const [textDraft, setTextDraft] = useState("");
  const [pickedFont, setPickedFont] = useState("outfit");
  const [pickedColor, setPickedColor] = useState("#FFD700");
  const [pickedSize, setPickedSize] = useState(32);
  const [selectedId, setSelectedId] = useState(null); // for editing timing/animation of selected overlay
  const [videoDur, setVideoDur] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed, src]);

  const filterCss = (FILTERS.find(f => f.id === filterId) || FILTERS[0]).css;

  const onLoadedMeta = () => {
    const d = videoRef.current?.duration || 0;
    setVideoDur(Number.isFinite(d) ? d : 0);
  };
  const onTimeUpdate = () => setCurrentTime(videoRef.current?.currentTime || 0);

  const addOverlay = (extra) => {
    const o = {
      id: uid(),
      x: 50, y: 50,
      start_time: 0, end_time: Math.max(3, Math.round(videoDur || 15)),
      animation: "none",
      ...extra,
    };
    setOverlays(list => [...list, o]);
    setSelectedId(o.id);
  };
  const addText = () => {
    if (!textDraft.trim()) return;
    addOverlay({
      type: "text", text: textDraft.slice(0, 80),
      font: pickedFont, color: pickedColor, fontSize: pickedSize,
    });
    setTextDraft("");
  };
  const addSticker = (s) => addOverlay({ type: "sticker", text: s, fontSize: 56 });
  const addGif = (g) => addOverlay({ type: "gif", url: g.url, fontSize: 120 });
  const removeOverlay = (id) => {
    setOverlays(o => o.filter(x => x.id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const patchOverlay = (id, patch) => setOverlays(o => o.map(x => x.id === id ? { ...x, ...patch } : x));

  const save = () => {
    onSave?.({
      filter_id: filterId,
      filter_css: filterCss,
      speed,
      overlays,
    });
  };

  const selected = overlays.find(o => o.id === selectedId);

  return (
    <div className="fixed inset-0 z-[80] bg-black flex flex-col" data-testid="video-editor">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center gap-2 px-3 py-2 bg-gradient-to-b from-black/85 to-transparent">
        <button onClick={onClose} data-testid="video-editor-close" className="p-2 rounded-full bg-white/10 active:scale-90"><X className="w-4 h-4"/></button>
        <div className="flex-1 text-center text-sm font-bold">Edit · Filters · Speed · Text · Emoji · GIFs</div>
        <button onClick={save} data-testid="video-editor-save" className="px-3 py-2 rounded-full bg-gradient-to-r from-[#A855F7] to-[#FF2A5F] text-white text-xs font-extrabold flex items-center gap-1 active:scale-95"><Check className="w-3.5 h-3.5"/>Save</button>
      </div>

      {/* Video preview */}
      <div className="absolute inset-0 bg-black flex items-center justify-center">
        {src && (
          <video ref={videoRef} src={src} autoPlay muted playsInline loop
            style={{ filter: filterCss }}
            className="max-h-full max-w-full"
            onLoadedMetadata={onLoadedMeta}
            onTimeUpdate={onTimeUpdate}
            data-testid="video-editor-preview"/>
        )}
        {overlays.map(o => {
          const active = o.id === selectedId;
          const font = FONTS.find(f => f.id === o.font) || FONTS[0];
          // Preview timing: hide overlay outside its time window
          const withinTime = currentTime >= (o.start_time || 0) && currentTime <= (o.end_time || 9999);
          if (!withinTime && !active) return null;
          const style = {
            left: `${o.x}%`, top: `${o.y}%`,
            transform: "translate(-50%,-50%)",
            color: o.color || "#fff",
            fontSize: (o.fontSize || 32) + "px",
            fontFamily: o.type === "text" ? font.css : undefined,
            fontWeight: o.type === "text" ? font.weight : undefined,
            textShadow: o.type === "text" ? "0 2px 12px rgba(0,0,0,0.85)" : "none",
            touchAction: "none",
            outline: active ? "2px dashed #00E5FF" : "none",
            outlineOffset: 4,
          };
          return (
            <div key={o.id}
              onPointerDown={(e) => {
                setSelectedId(o.id);
                e.currentTarget.setPointerCapture(e.pointerId);
                dragRef.current = { id: o.id, lastX: e.clientX, lastY: e.clientY };
              }}
              onPointerMove={(e) => {
                const d = dragRef.current; if (!d || d.id !== o.id) return;
                const parent = e.currentTarget.parentElement.getBoundingClientRect();
                const dx = e.clientX - d.lastX, dy = e.clientY - d.lastY;
                dragRef.current = { id: o.id, lastX: e.clientX, lastY: e.clientY };
                patchOverlay(o.id, {
                  x: Math.max(0, Math.min(100, o.x + (dx/parent.width)*100)),
                  y: Math.max(0, Math.min(100, o.y + (dy/parent.height)*100)),
                });
              }}
              onPointerUp={() => { dragRef.current = null; }}
              className={`absolute select-none cursor-move drop-shadow-lg ${o.type === "text" ? "font-extrabold" : ""}`}
              style={style}
              data-testid={`ve-overlay-${o.id}`}>
              {o.type === "gif" ? (
                <img src={o.url} alt="gif" draggable={false} style={{ width: (o.fontSize || 120), height: "auto" }}/>
              ) : (
                o.text
              )}
              {active && (
                <button onClick={(e) => { e.stopPropagation(); removeOverlay(o.id); }}
                  className="absolute -top-3 -right-3 w-5 h-5 rounded-full bg-[#FF2A5F] text-white text-[11px] flex items-center justify-center">×</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Controls overlay */}
      <div className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-black via-black/85 to-transparent pt-6 px-3 pb-4 space-y-3 z-20">
        {/* Selected overlay timing controls */}
        {selected && (
          <div className="rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/40 p-2.5 space-y-2" data-testid="ve-selected-panel">
            <div className="flex items-center gap-2 text-[10px] font-bold text-[#00E5FF]">
              <Clock className="w-3.5 h-3.5"/>
              Timing · shows from {(selected.start_time||0).toFixed(1)}s to {(selected.end_time||0).toFixed(1)}s
              {videoDur > 0 && <span className="text-white/60">/ {videoDur.toFixed(1)}s</span>}
            </div>
            <div className="flex items-center gap-2">
              <input type="range" min={0} max={Math.max(1, videoDur)} step={0.1}
                value={selected.start_time || 0}
                onChange={e => patchOverlay(selected.id, { start_time: Math.min(parseFloat(e.target.value), (selected.end_time || videoDur) - 0.1) })}
                data-testid="ve-timing-start" className="flex-1 accent-[#00E5FF]"/>
              <input type="range" min={0} max={Math.max(1, videoDur)} step={0.1}
                value={selected.end_time || videoDur}
                onChange={e => patchOverlay(selected.id, { end_time: Math.max(parseFloat(e.target.value), (selected.start_time || 0) + 0.1) })}
                data-testid="ve-timing-end" className="flex-1 accent-[#FF2A5F]"/>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-white/60 font-bold flex items-center gap-1"><Zap className="w-3 h-3"/>Animate:</span>
              {ANIMATIONS.map(a => (
                <button key={a.id} onClick={() => patchOverlay(selected.id, { animation: a.id })}
                  data-testid={`ve-anim-${a.id}`}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${selected.animation === a.id ? "bg-[#A855F7] text-white" : "bg-white/10 text-white/70"}`}>{a.name}</button>
              ))}
            </div>
            {selected.type === "text" && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-white/60 font-bold">Size:</span>
                <input type="range" min={14} max={96} step={1} value={selected.fontSize || 32}
                  onChange={e => patchOverlay(selected.id, { fontSize: parseInt(e.target.value, 10) })}
                  data-testid="ve-fontsize" className="flex-1 accent-[#FF7A00]"/>
                <span className="text-[10px] text-white/80 w-8 text-right">{selected.fontSize || 32}px</span>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1.5">
          {[
            { id: "filter",  icon: Sparkles, label: "Filter" },
            { id: "speed",   icon: Gauge,    label: "Speed" },
            { id: "text",    icon: Type,     label: "Text" },
            { id: "sticker", icon: Smile,    label: "Sticker" },
            { id: "gif",     icon: ImagePlay,label: "GIF" },
          ].map(t => {
            const Ic = t.icon; const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ve-tab-${t.id}`}
                className={`flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-xl text-[10px] font-bold active:scale-95 ${active ? "bg-[#A855F7] text-white" : "bg-white/8 text-white/70"}`}>
                <Ic className="w-3.5 h-3.5"/>{t.label}
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl bg-black/55 backdrop-blur border border-white/10 p-2.5 max-h-[210px] overflow-y-auto">
          {tab === "filter" && (
            <div className="flex gap-1.5 overflow-x-auto">
              {FILTERS.map(f => (
                <button key={f.id} onClick={() => setFilterId(f.id)} data-testid={`ve-filter-${f.id}`}
                  className={`flex-shrink-0 px-3 py-2 rounded-xl text-[10px] font-bold active:scale-95 border ${filterId === f.id ? "bg-[#A855F7] text-white border-[#A855F7]" : "bg-white/5 text-white/80 border-white/10"}`}>{f.name}</button>
              ))}
            </div>
          )}
          {tab === "speed" && (
            <div className="flex gap-1.5">
              {[0.5, 1, 2].map(s => (
                <button key={s} onClick={() => setSpeed(s)} data-testid={`ve-speed-${s}`}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold active:scale-95 ${speed === s ? "bg-[#FF7A00] text-black" : "bg-white/8 text-white/80"}`}>{s}x</button>
              ))}
            </div>
          )}
          {tab === "text" && (
            <div className="space-y-2">
              {/* Font picker */}
              <div className="flex gap-1.5 overflow-x-auto">
                {FONTS.map(f => (
                  <button key={f.id} onClick={() => setPickedFont(f.id)} data-testid={`ve-font-${f.id}`}
                    style={{ fontFamily: f.css, fontWeight: f.weight }}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs active:scale-95 ${pickedFont === f.id ? "bg-[#FF7A00] text-black" : "bg-white/5 text-white/85 border border-white/10"}`}>Aa {f.name}</button>
                ))}
              </div>
              {/* Colors */}
              <div className="flex gap-1.5 items-center flex-wrap">
                <Palette className="w-3.5 h-3.5 text-white/50"/>
                {COLORS.map(c => (
                  <button key={c} onClick={() => setPickedColor(c)} data-testid={`ve-color-${c.replace("#","")}`}
                    style={{ background: c }}
                    className={`w-6 h-6 rounded-full border ${pickedColor === c ? "ring-2 ring-[#00E5FF] border-white" : "border-white/20"}`}/>
                ))}
                <input type="range" min={16} max={80} step={1} value={pickedSize}
                  onChange={e => setPickedSize(parseInt(e.target.value, 10))}
                  data-testid="ve-size-picker" className="ml-auto w-20 accent-[#00E5FF]"/>
              </div>
              {/* Text input + Add */}
              <div className="flex items-center gap-2">
                <input value={textDraft} onChange={e => setTextDraft(e.target.value)} placeholder="Type text (up to 80 chars)…"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addText(); } }}
                  data-testid="ve-text-input"
                  style={{ fontFamily: (FONTS.find(f => f.id === pickedFont) || FONTS[0]).css }}
                  className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
                <button onClick={addText} data-testid="ve-text-add"
                  className="px-3 py-2 rounded-xl bg-[#FF7A00] text-black font-bold text-xs active:scale-95">+ Add</button>
              </div>
            </div>
          )}
          {tab === "sticker" && (
            <div className="flex flex-wrap gap-1.5">
              {STICKERS.map(s => (
                <button key={s} onClick={() => addSticker(s)} data-testid={`ve-sticker-${s}`}
                  className="text-2xl p-1.5 rounded-lg bg-white/5 hover:bg-white/15 active:scale-90">{s}</button>
              ))}
            </div>
          )}
          {tab === "gif" && (
            <div className="grid grid-cols-4 gap-1.5">
              {GIFS.map(g => (
                <button key={g.id} onClick={() => addGif(g)} data-testid={`ve-gif-${g.id}`}
                  className="rounded-lg bg-white/5 border border-white/10 overflow-hidden active:scale-95 aspect-square">
                  <img src={g.url} alt={g.label} className="w-full h-full object-cover" loading="lazy"/>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
